import { Observable, Subject, Subscription, iif, throwError } from "rxjs";
import { catchError, first, switchMap } from "rxjs/operators";
import { DateTime } from 'luxon'

import { SearchQuery, DataStoreOptions, DataSynchronisationStatus, DataSynchronizerStats, DataSynchroEvent } from "@skinsoft/types";
import { SynchroDownHandler } from "@skinsoft/classes-interfaces-definition";

export const dataSynchroEventBus$ = new Subject<DataSynchroEvent>()

declare const config
const SEARCH_PAGE_SIZE = config?.syncDownPageSize?.default ?? 500

/**
 * @type T Type of entity fetched from API
 * @type R Type of response from synch down request
 * @type U type of entity to save
 */
export abstract class AbstractSyncDownHandler<T = any, R = any, U = any> implements SynchroDownHandler {
    protected currentNbSychronized: number = 0
    protected executionStartTime: DateTime | null = null
    protected maxNbToSynchronize: number = Infinity
    protected shouldAbortSync: boolean = false
    protected syncing: boolean = false
    protected currentSynchroSubscription: Subscription | null = null
    protected typesForSyncDown: string[]
    protected emulateSearch: boolean = false
    protected searchPageSize: number

    constructor(typesForSyncDown: string[], searchPageSize: number = SEARCH_PAGE_SIZE) {
        this.typesForSyncDown = typesForSyncDown
        this.searchPageSize = searchPageSize
    }

    abstract get mainCacheKey(): string
    
    abstract clearCacheDown(): Promise<void>
    abstract beforeSynchDown(): Promise<void>
    
    async synchronize(pageIndex: number, clearBefore?: boolean | undefined, firstCall?: boolean | undefined): Promise<void> {
        if (clearBefore) {
            await this.clearCacheDown()
            this.currentNbSychronized = 0
        }
        if (firstCall) {
            await this.beforeSynchDown()
        }

        if(this.shouldAbortSync) {
            this.onAbortionAsked()
        } 
        else {
            // Freeing last subscription
            if (this.currentSynchroSubscription) {
                this.currentSynchroSubscription.unsubscribe()
            }

            const searchResponse = await iif(
                () => window.navigator.onLine,
                this.sendSynchroDownRequest(
                    {
                        queryText: "",
                        page: pageIndex,
                        pageSize: this.searchPageSize,
                        types: this.typesForSyncDown,
                    },
                    {
                        emulateSearch: this.emulateSearch
                    },
                ),
                throwError("Synchronization cannot be performed in offline mode")
            ).pipe(
                first(),
                catchError(
                    (error) => {
                        this.saveStats(DataSynchronisationStatus.ERROR)
                        this.syncing = false;
                        dataSynchroEventBus$.next({
                            name: this.mainCacheKey,
                            status: DataSynchronisationStatus.ERROR,
                        })
                        throw error
                    }
                )
            ).toPromise()

            // Retrieving total number of results for stats
            if (this.maxNbToSynchronize === Infinity) {
                this.maxNbToSynchronize = this.getTotalNbToSync(searchResponse)
            }

            const nbResultsInResponse = this.getNbResultsInResponse(searchResponse)

            // Appending results if there are ones, ending syncing otherwise
            if (nbResultsInResponse > 0) {

                const data = this.unpackSynchDownResponse(searchResponse)
                const dataToCache = this.formatDataForCaching(data)
                await this.saveSynchDownDataInCache(dataToCache)

                if (this.emulateSearch) {
                    this.currentNbSychronized += 1;
                } else {
                    this.currentNbSychronized += nbResultsInResponse
                }
                this.saveStats(DataSynchronisationStatus.SUCCESS)
                await this.synchronize(pageIndex + 1, false, false)

            } else {
                dataSynchroEventBus$.next({
                    name: this.mainCacheKey,
                    status: DataSynchronisationStatus.SUCCESS,
                })
                this.saveStats(DataSynchronisationStatus.SUCCESS)
                this.syncing = false
            }
        }
    }

    abstract sendSynchroDownRequest(searchQuery: SearchQuery, options: DataStoreOptions): Observable<R>
    abstract getTotalNbToSync(response: R): number
    abstract getNbResultsInResponse(response: R): number
    abstract unpackSynchDownResponse(response: R): T[]
    abstract formatDataForCaching(rawData: T[]): U[]
    abstract saveSynchDownDataInCache(data: U[]): Promise<void>
    abstract saveStats(status: DataSynchronisationStatus): void

    protected onAbortionAsked() {
        this.saveStats(DataSynchronisationStatus.ABORTED)

        this.shouldAbortSync = false
        this.syncing = false
        dataSynchroEventBus$.next({
            name: this.mainCacheKey,
            status: DataSynchronisationStatus.ABORTED,
        })      
    }

    abstract abortSynchronisation(): void
    abstract isSyncing(): boolean
    abstract getLastSyncStats(): DataSynchronizerStats | null
} 